import { useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import PlaceIcon from '@mui/icons-material/Place';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EuroIcon from '@mui/icons-material/Euro';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import UpdateIcon from '@mui/icons-material/Update';
import LanguageIcon from '@mui/icons-material/Language';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { LunchItem, Restaurant } from '../types';

interface RestaurantCardProps {
  restaurant: Restaurant;
  index?: number;
  isFavorite?: boolean;
  onToggleFavorite?: (restaurantId: string) => void;
  onLocate?: (lat: number, lng: number) => void;
}

/** Get today's date as YYYY-MM-DD */
function getTodayStr(): string {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
}

/** Group lunch items by date, filtering to today and future only */
function groupByDate(items: LunchItem[]): Record<string, LunchItem[]> {
  const todayStr = getTodayStr();
  const groups: Record<string, LunchItem[]> = {};
  for (const item of items) {
    const key = item.date || 'unknown';
    if (key < todayStr && key !== 'unknown') continue;
    if (!groups[key]) groups[key] = [];
    groups[key]!.push(item);
  }
  return groups;
}

/** Format a date string (YYYY-MM-DD) to a Finnish day label */
function formatDateLabel(dateStr: string): string {
  try {
    const date = new Date(dateStr + 'T12:00:00');
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'tänään';
    if (date.toDateString() === tomorrow.toDateString()) return 'huomenna';

    return date.toLocaleDateString('fi-FI', { weekday: 'long', day: 'numeric', month: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatDistance(km: number | undefined): string {
  if (km === undefined) return '';
  return `${km.toFixed(1).replace('.', ',')} km`;
}

function formatPrice(price: number): string {
  return `${price.toFixed(2).replace('.', ',')} €`;
}

function formatUpdatedAt(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'juuri nyt';
  if (diffMins < 60) return `${diffMins} min sitten`;
  if (diffHours < 24) return `${diffHours} h sitten`;
  if (diffDays === 1) return 'eilen';
  if (diffDays < 7) return `${diffDays} pv sitten`;
  return date.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric' });
}

const CATEGORY_COLORS: Record<string, string> = {
  Buffet: '#e8f5e9',
  Kotiruoka: '#fff3e0',
  Thai: '#fce4ec',
  Pizza: '#fff8e1',
  Grilli: '#efebe9',
  Kasvis: '#e8f5e9',
  Kala: '#e3f2fd',
  Keitto: '#f3e5f5',
};

export default function RestaurantCard({ restaurant, index = 0, isFavorite = false, onToggleFavorite, onLocate }: RestaurantCardProps) {
  const { name, distance, price, lunchItems, openingHours, categories, coordinates, updatedAt, website } = restaurant;
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}`;

  // Compute grouped dates for tabs
  const grouped = lunchItems && lunchItems.length > 0 ? groupByDate(lunchItems) : {};
  const dates = Object.keys(grouped).sort();
  const todayStr = getTodayStr();
  const todayIndex = dates.indexOf(todayStr);
  const initialTab = todayIndex >= 0 ? todayIndex : 0;
  const [activeTab, setActiveTab] = useState(initialTab);

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        animation: 'fadeSlideIn 0.3s ease forwards',
        animationDelay: `${index * 50}ms`,
        opacity: 0,
        '@keyframes fadeSlideIn': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <CardContent sx={{ pb: '12px !important', px: 2.5, pt: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
            <Typography variant="subtitle1" component="h3" sx={{ lineHeight: 1.3 }}>
              {name}
            </Typography>
            {website && (
              <Tooltip title="Kotisivut">
                <IconButton
                  size="small"
                  color="primary"
                  component="a"
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Avaa ${name} kotisivut`}
                  sx={{ ml: 0.5 }}
                >
                  <LanguageIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
            {distance !== undefined && (
              <Tooltip title="Navigoi (Google Maps)">
                <Chip
                  icon={<PlaceIcon sx={{ fontSize: 14 }} />}
                  label={formatDistance(distance)}
                  size="small"
                  variant="outlined"
                  color="primary"
                  component="a"
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  sx={{ fontWeight: 500, minHeight: 28 }}
                  aria-label={`Navigoi: ${name} (${formatDistance(distance)})`}
                />
              </Tooltip>
            )}
            {onLocate && (
              <Tooltip title="Näytä kartalla">
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={`Näytä kartalla: ${name}`}
                  onClick={() => onLocate(coordinates.lat, coordinates.lng)}
                >
                  <MyLocationIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {onToggleFavorite && (
              <Tooltip title={isFavorite ? 'Poista suosikeista' : 'Lisää suosikkeihin'}>
                <IconButton
                  size="small"
                  color={isFavorite ? 'error' : 'default'}
                  aria-label={isFavorite ? `Poista suosikeista: ${name}` : `Lisää suosikkeihin: ${name}`}
                  onClick={() => onToggleFavorite(restaurant.restaurantId)}
                >
                  {isFavorite ? <FavoriteIcon sx={{ fontSize: 18 }} /> : <FavoriteBorderIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Meta row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EuroIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
            <Typography variant="body2" fontWeight={600} color="secondary.dark">
              {price !== null ? formatPrice(price) : 'Hinta ei saatavilla.'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {openingHours ?? 'Aukioloajat eivät ole saatavilla.'}
            </Typography>
          </Box>
          <Tooltip title={`Lounastiedot päivitetty: ${new Date(updatedAt).toLocaleString('fi-FI')}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <UpdateIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {formatUpdatedAt(updatedAt)}
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        {/* Lunch items with daily tabs */}
        {dates.length > 0 ? (
          <>
            <Divider sx={{ mb: 1 }} />
            {dates.length > 1 && (
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 32,
                  mb: 1,
                  '& .MuiTab-root': {
                    minHeight: 32,
                    py: 0.5,
                    px: 1.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                  },
                }}
              >
                {dates.map((date) => (
                  <Tab key={date} label={formatDateLabel(date)} />
                ))}
              </Tabs>
            )}
            {dates.length === 1 && (
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5, textTransform: 'capitalize' }}
              >
                {formatDateLabel(dates[0]!)}
              </Typography>
            )}
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 1 }}>
              {(grouped[dates[activeTab]!] ?? []).map((item, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  <Typography variant="body2">{item.name}</Typography>
                  {item.calories && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                      <LocalFireDepartmentIcon sx={{ fontSize: 13, color: 'warning.main' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        ~{Math.round(item.calories.value)} kcal
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Lounastiedot eivät ole saatavilla.
          </Typography>
        )}

        {/* Categories */}
        {categories.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                size="small"
                sx={{
                  bgcolor: CATEGORY_COLORS[cat] ?? 'grey.100',
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  height: 24,
                  minHeight: 24,
                }}
              />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
